import type { MessageHandler } from "@sumsub/websdk";
import SumsubWidget from "@sumsub/websdk-react";
import { type FC, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Link, Loading } from "src/components";
import { useKycTokenQuery, useUserQuery } from "src/serverApi";
import { useTranslation } from "src/translations";
import { isNullish, logGtmEvent } from "src/utils";
import { KYC_CHECKING_ROUTE, KYC_SELECTION_ROUTE } from "../utils";

export const SumSub: FC = () => {
    const { t, i18n } = useTranslation();
    const { data: user, isLoading: isUserLoading } = useUserQuery(undefined, { pollingInterval: undefined });
    const { data: kycData, isLoading: isKycDataLoading, isSuccess: isKycDataSuccess } = useKycTokenQuery();

    const navigate = useNavigate();
    const sumSubToken = kycData?.token;

    const sumSubConfig = useMemo(
        () => ({
            lang: i18n.language,
            email: user?.email,
            theme: "light",
        }),
        [i18n.language, user?.email],
    );

    const messageHandler: MessageHandler = async (type, payload) => {
        if (type === "idCheck.onStepInitiated") {
            // @ts-ignore
            const step = payload?.idDocSetType;
            const stepFormatted = step?.toLowerCase();
            logGtmEvent({ event: `user_kyc_step_initiated_${stepFormatted}` });
        }
        if (type === "idCheck.onApplicantStatusChanged") {
            // This looks like a custom response from Sumsub
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const payloadData = payload as any;
            if (payloadData.reviewStatus === "completed" && payloadData.reviewResult?.reviewAnswer === "GREEN") {
                navigate(KYC_CHECKING_ROUTE);
            }
        }
    };

    useEffect(() => {
        if (isKycDataSuccess && isNullish(sumSubToken)) {
            navigate(KYC_CHECKING_ROUTE);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isKycDataLoading, sumSubToken]);

    if (isUserLoading || isKycDataLoading || isNullish(sumSubToken)) {
        return <Loading />;
    }

    return (
        <div className="grid size-full items-center justify-center">
            <div className="mt-12 grid size-full items-center justify-center gap-8">
                <div className="md:w-[600px]">
                    <Link href={KYC_SELECTION_ROUTE}>← {t("verification.KYC.backToSelection")}</Link>
                    <SumsubWidget
                        accessToken={sumSubToken}
                        config={sumSubConfig}
                        expirationHandler={() => Promise.resolve(sumSubToken)}
                        options={{ adaptIframeHeight: true }}
                        onMessage={messageHandler}
                    />
                </div>
            </div>
        </div>
    );
};
